import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { MainLayout } from '@/layouts/MainLayout';
import { PageErrorBoundary } from '@/components/PageErrorBoundary';
import { Login } from '@/pages/Login';
import { Dashboard } from '@/pages/Dashboard';
import { NotFound } from '@/pages/NotFound';
import { Companies, Roles, Users, PartnerNetworks, AuditLogs } from '@/pages/admin';

// Core module pages
import Locations from '@/pages/Locations';
import LocationsNew from '@/pages/LocationsNew';
import LocationsEdit from '@/pages/LocationsEdit';
import LocationDetail from '@/pages/LocationDetail';
import Suppliers from '@/pages/Suppliers';
import SuppliersNew from '@/pages/SuppliersNew';
import SuppliersEdit from '@/pages/SuppliersEdit';
import SupplierDetail from '@/pages/SupplierDetail';
import Inventory from '@/pages/Inventory';
import InventoryNew from '@/pages/InventoryNew';
import InventoryEdit from '@/pages/InventoryEdit';
import InventoryDetail from '@/pages/InventoryDetail';
import CertificationGaps from '@/pages/equipment/CertificationGaps';
import Guide from '@/pages/Guide';
import Jobs from '@/pages/Jobs';
import Wells from '@/pages/Wells';
import WellDetail from '@/pages/WellDetail';
import JobDetail from '@/pages/JobDetail';
import CriticalSpares from '@/pages/inventory/CriticalSpares';
import Consignment from '@/pages/Consignment';
import CallOffDetail from '@/pages/CallOffDetail';
import Portal from '@/pages/portal/Portal';
import SupplyAnalytics from '@/pages/supply/SupplyAnalytics';
import SiteOptimization from '@/pages/inventory/SiteOptimization';
import CycleCounts from '@/pages/inventory/CycleCounts';
import Equipment from '@/pages/Equipment';
import EquipmentNew from '@/pages/EquipmentNew';
import EquipmentEdit from '@/pages/EquipmentEdit';
import RotableAssets from '@/pages/equipment/RotableAssets';
import AssetsOnJobs from '@/pages/equipment/AssetsOnJobs';
import Rentals from '@/pages/equipment/Rentals';
import RentalDetail from '@/pages/equipment/RentalDetail';
import Permits from '@/pages/permits/Permits';
import PermitCaseDetail from '@/pages/permits/PermitCaseDetail';
import PermitWatchlist from '@/pages/permits/PermitWatchlist';
import PermitCountries from '@/pages/permits/PermitCountries';
import EquipmentDetail from '@/pages/EquipmentDetail';
import EquipmentHierarchy from '@/pages/EquipmentHierarchy';
import PurchaseOrders from '@/pages/PurchaseOrders';
import PurchaseOrdersNew from '@/pages/PurchaseOrdersNew';
import PurchaseOrderDetail from '@/pages/PurchaseOrderDetail';
import Shipments from '@/pages/Shipments';
import ShipmentsNew from '@/pages/ShipmentsNew';
import ShipmentDetail from '@/pages/ShipmentDetail';
import Alerts from '@/pages/Alerts';
import Approvals from '@/pages/Approvals';
import Analytics from '@/pages/Analytics';

// Phase 2.3 pages
import QuickScan from '@/pages/QuickScan';
import ConsumptionTracking from '@/pages/ConsumptionTracking';
import ConsumptionRecordDetail from '@/pages/ConsumptionRecordDetail';
import ConsumptionTrackingNew from '@/pages/ConsumptionTrackingNew';
import MaintenanceSchedules from '@/pages/MaintenanceSchedules';
import MaintenanceSchedulesNew from '@/pages/MaintenanceSchedulesNew';
import MaintenanceScheduleDetail from '@/pages/MaintenanceScheduleDetail';
import WorkOrders from '@/pages/WorkOrders';
import WorkOrdersNew from '@/pages/WorkOrdersNew';
import WorkOrderDetail from '@/pages/WorkOrderDetail';
import GoodsReceipt from '@/pages/GoodsReceipt';
import GoodsReceiptNew from '@/pages/GoodsReceiptNew';
import GoodsReceiptDetail from '@/pages/GoodsReceiptDetail';
import ComplianceNew from '@/pages/ComplianceNew';
import RiskManagement from '@/pages/RiskManagement';
import RiskProfileDetail from '@/pages/RiskProfileDetail';
import Certificates from '@/pages/Certificates';
import CertificateDetail from '@/pages/CertificateDetail';

// Phase 2.4 pages
import Forecasting from '@/pages/Forecasting';
import ForecastDetail from '@/pages/ForecastDetail';
import POVisibility from '@/pages/POVisibility';
import {
  YardDashboard,
  YardStorageLocations,
  YardPutaway,
  YardJobKits,
  YardPicking,
  YardPipeTally,
  YardAssetSearch,
} from '@/pages/yard';

// Phase 3: TMS pages
import {
  TMSDashboard,
  ShipmentPlanner,
  LoadBuilder,
  TrackTrace,
  Carriers,
  CarrierDetail,
  FreightRFQ,
  FreightRFQCreate,
  FreightRFQDetail,
  FreightRFQCompare,
  FreightRFQAnalytics,
  FreightRFQApprovals,
  FreightRFQApprovalConfig,
  Rates,
  RateDashboard,
  RateBulkImport,
  RateAnalytics,
  RateTemplates,
  ChargeTypes,
  VendorGroups,
  TermsConditions,
} from '@/pages/tms';
import CarriersNew from '@/pages/tms/CarriersNew';
import ChargeTypesNew from '@/pages/tms/ChargeTypesNew';
import VendorGroupsNew from '@/pages/tms/VendorGroupsNew';
import TermsConditionsNew from '@/pages/tms/TermsConditionsNew';
import YardStorageLocationsNew from '@/pages/yard/YardStorageLocationsNew';
import YardJobKitsNew from '@/pages/yard/YardJobKitsNew';
import YardJobKitDetail from '@/pages/yard/YardJobKitDetail';
import YardPickListDetail from '@/pages/yard/YardPickListDetail';
import YardPipeTallyDetail from '@/pages/yard/YardPipeTallyDetail';

// Figma Design System Example
import DesignSystem from '@/pages/DesignSystem';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}


/**
 * Where "home" is depends on who signed in.
 *
 * A customer-portal login has `portal:read` and nothing else, so the dashboard
 * fires eight internal endpoints that all 403 — correctly refused, and a broken
 * first impression made of thirty-six console errors. The landing route asks
 * what the user can actually see instead of assuming everyone is internal.
 */
function Home() {
  const { hasPermission } = useAuth();
  if (hasPermission('portal:read') && !hasPermission('inventory:read')) {
    return <Navigate to="/portal" replace />;
  }
  return <Dashboard />;
}

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PageErrorBoundary>
              <MainLayout />
            </PageErrorBoundary>
          </ProtectedRoute>
        }
      >
        <Route index element={<Home />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Core module routes - implemented */}
        <Route path="inventory" element={<Inventory />} />
        <Route path="inventory/new" element={<InventoryNew />} />
        <Route path="inventory/critical-spares" element={<CriticalSpares />} />
        <Route path="consignment" element={<Consignment />} />
        <Route path="consignment/call-offs/:id" element={<CallOffDetail />} />
        <Route path="portal" element={<Portal />} />
        <Route path="supply-analytics" element={<SupplyAnalytics />} />
        <Route path="inventory/site-optimization" element={<SiteOptimization />} />
        <Route path="inventory/cycle-counts" element={<CycleCounts />} />
        <Route path="inventory/:id/edit" element={<InventoryEdit />} />
        <Route path="inventory/:id" element={<InventoryDetail />} />
        <Route path="guide" element={<Guide />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="wells" element={<Wells />} />
        <Route path="wells/:id" element={<WellDetail />} />
        <Route path="jobs/:id" element={<JobDetail />} />
        <Route path="consumption" element={<ConsumptionTracking />} />
        <Route path="consumption/new" element={<ConsumptionTrackingNew />} />
        {/* Variance and approvals are tabs on the consumption page now. The
            routes stay so existing links, bookmarks and the module registry
            keep working — they land on the right tab rather than 404. */}
        <Route
          path="consumption/variance"
          element={<Navigate to="/consumption?tab=variance" replace />}
        />
        <Route
          path="consumption/approvals"
          element={<Navigate to="/consumption?tab=approvals" replace />}
        />
        {/* After the literal consumption routes: :id would otherwise swallow
            "approvals", "variance" and "new". */}
        <Route path="consumption/:id" element={<ConsumptionRecordDetail />} />
        <Route path="quick-scan" element={<QuickScan />} />

        <Route path="equipment" element={<Equipment />} />
        <Route path="equipment/new" element={<EquipmentNew />} />
        <Route path="equipment/hierarchy" element={<EquipmentHierarchy />} />
        <Route path="equipment/rotable-assets" element={<RotableAssets />} />
        <Route path="equipment/out-on-jobs" element={<AssetsOnJobs />} />
        <Route path="equipment/rentals" element={<Rentals />} />
        <Route path="equipment/rentals/:id" element={<RentalDetail />} />
        <Route path="equipment/certification-gaps" element={<CertificationGaps />} />
        <Route path="permits" element={<Permits />} />
        <Route path="permits/countries" element={<PermitCountries />} />
        <Route path="permits/watchlist" element={<PermitWatchlist />} />
        <Route path="permits/:id" element={<PermitCaseDetail />} />
        <Route path="equipment/:id/edit" element={<EquipmentEdit />} />
        <Route path="equipment/:id" element={<EquipmentDetail />} />
        <Route path="work-orders" element={<WorkOrders />} />
        <Route path="work-orders/new" element={<WorkOrdersNew />} />
        <Route path="work-orders/:id" element={<WorkOrderDetail />} />
        <Route path="maintenance" element={<MaintenanceSchedules />} />
        <Route path="maintenance/new" element={<MaintenanceSchedulesNew />} />
        <Route path="maintenance/:id" element={<MaintenanceScheduleDetail />} />

        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="purchase-orders/new" element={<PurchaseOrdersNew />} />
        <Route path="purchase-orders/:id" element={<PurchaseOrderDetail />} />
        <Route path="goods-receipt" element={<GoodsReceipt />} />
        {/* `new` stays above `:id` — declaration order decides, and a literal
            below the parameter is read as an id. */}
        <Route path="goods-receipt/new" element={<GoodsReceiptNew />} />
        <Route path="goods-receipt/:id" element={<GoodsReceiptDetail />} />
        <Route path="po-visibility" element={<POVisibility />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="suppliers/new" element={<SuppliersNew />} />
        <Route path="suppliers/:id/edit" element={<SuppliersEdit />} />
        <Route path="suppliers/:id" element={<SupplierDetail />} />

        <Route path="tms" element={<TMSDashboard />} />
        <Route path="shipment-planner" element={<ShipmentPlanner />} />
        <Route path="shipments" element={<Shipments />} />
        <Route path="shipments/new" element={<ShipmentsNew />} />
        <Route path="shipments/:id" element={<ShipmentDetail />} />
        <Route path="tms/track-trace" element={<TrackTrace />} />

        {/* TMS - Carriers */}
        <Route path="tms/carriers" element={<Carriers />} />
        <Route path="tms/carriers/new" element={<CarriersNew />} />
        <Route path="tms/carriers/:id" element={<CarrierDetail />} />

        {/* TMS - Freight RFQ */}
        <Route path="tms/rfq" element={<FreightRFQ />} />
        <Route path="tms/rfq/new" element={<FreightRFQCreate />} />
        <Route path="tms/rfq/:id" element={<FreightRFQDetail />} />
        <Route path="tms/rfq/:id/compare" element={<FreightRFQCompare />} />
        <Route path="tms/rfq/:id/approval-config" element={<FreightRFQApprovalConfig />} />
        <Route path="tms/rfq-approvals" element={<FreightRFQApprovals />} />
        <Route path="tms/rfq-analytics" element={<FreightRFQAnalytics />} />

        {/* TMS - Load Builder */}
        <Route path="tms/load-builder" element={<LoadBuilder />} />

        {/* TMS - Rate Engine */}
        <Route path="tms/rates" element={<Rates />} />
        <Route path="tms/rate-dashboard" element={<RateDashboard />} />
        <Route path="tms/rate-import" element={<RateBulkImport />} />
        <Route path="tms/rate-analytics" element={<RateAnalytics />} />
        <Route path="tms/rate-templates" element={<RateTemplates />} />

        {/* TMS - Master Data */}
        <Route path="tms/charge-types" element={<ChargeTypes />} />
        <Route path="tms/charge-types/new" element={<ChargeTypesNew />} />
        <Route path="tms/vendor-groups" element={<VendorGroups />} />
        <Route path="tms/vendor-groups/new" element={<VendorGroupsNew />} />
        <Route path="tms/terms" element={<TermsConditions />} />
        <Route path="tms/terms/new" element={<TermsConditionsNew />} />

        <Route path="yard" element={<YardDashboard />} />
        <Route path="yard/locations" element={<YardStorageLocations />} />
        <Route path="yard/locations/new" element={<YardStorageLocationsNew />} />
        <Route path="yard/putaway" element={<YardPutaway />} />
        <Route path="yard/job-kits" element={<YardJobKits />} />
        <Route path="yard/job-kits/new" element={<YardJobKitsNew />} />
        <Route path="yard/job-kits/:id" element={<YardJobKitDetail />} />
        <Route path="yard/picking" element={<YardPicking />} />
        <Route path="yard/picking/:id" element={<YardPickListDetail />} />
        <Route path="yard/pipe-tally" element={<YardPipeTally />} />
        <Route path="yard/pipe-tally/:id" element={<YardPipeTallyDetail />} />
        <Route path="yard/asset-search" element={<YardAssetSearch />} />

        <Route path="risk" element={<RiskManagement />} />
        <Route path="risk/:id" element={<RiskProfileDetail />} />
        {/* /compliance listed the same Certificate records as /certificates
            through a second fetch and a different set of columns. The two are
            one page now; the path stays so old links and bookmarks resolve. */}
        <Route path="compliance" element={<Navigate to="/certificates" replace />} />
        <Route path="compliance/new" element={<ComplianceNew />} />
        <Route path="certificates" element={<Certificates />} />
        <Route path="certificates/:id" element={<CertificateDetail />} />

        <Route path="analytics" element={<Analytics />} />
        <Route path="forecasting" element={<Forecasting />} />
        <Route path="forecasting/:id" element={<ForecastDetail />} />

        <Route path="approvals" element={<Approvals />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="locations" element={<Locations />} />
        <Route path="locations/new" element={<LocationsNew />} />
        <Route path="locations/:id/edit" element={<LocationsEdit />} />
        <Route path="locations/:id" element={<LocationDetail />} />

        {/* Admin routes */}
        <Route path="admin/companies" element={<Companies />} />
        <Route path="admin/roles" element={<Roles />} />
        <Route path="admin/users" element={<Users />} />
        <Route path="admin/partners" element={<PartnerNetworks />} />
        <Route path="admin/audit-logs" element={<AuditLogs />} />

        {/* Design System */}
        <Route path="design-system" element={<DesignSystem />} />

        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
